import { useEffect } from "react";
import { DIRECTION_CHECK_THRESHOLD } from "./mapConfig";
import { determineInitialDirection } from "./mapUtils";

const savePathDirection = (vehicleId, direction) => {
  const key = `pathDirection_${vehicleId}`;
  const value = {
    direction: direction,
    timestamp: Date.now(),
    vehicleId: vehicleId,
  };
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    console.error("Failed to save to localStorage:", error);
  }
};

const getPathDirection = (vehicleId) => {
  const key = `pathDirection_${vehicleId}`;
  const saved = localStorage.getItem(key);
  if (!saved) return null;
  try {
    const value = JSON.parse(saved);
    const ageInMinutes = (Date.now() - value.timestamp) / (1000 * 60);
    if (ageInMinutes > 60) {
      localStorage.removeItem(key);
      return null;
    }
    return value.direction;
  } catch (e) {
    return null;
  }
};

export const usePathDirection = ({
  vehicle,
  positionHistory,
  pathDirection,
  stops,
  calculateDistance,
  checkDirectionChange,
  checkJourneyComplete,
  setPathDirection,
  setPositionHistory,
  directionCheckRef,
}) => {
  // Restore saved direction
  useEffect(() => {
    if (vehicle?.device_id) {
      const savedDirection = getPathDirection(vehicle.device_id);
      if (savedDirection) {
        setPathDirection(savedDirection);
        console.log(
          `Restored direction for vehicle ${vehicle.device_id}: ${savedDirection}`
        );
      }
    }
  }, [vehicle?.device_id, setPathDirection]);

  // Clean up path direction on unmount
  useEffect(() => {
    return () => {
      if (vehicle?.device_id) {
        localStorage.removeItem(`pathDirection_${vehicle.device_id}`);
      }
      setPathDirection(null);
      setPositionHistory([]);
    };
  }, [vehicle?.device_id, setPathDirection, setPositionHistory]);

  // Handle direction changes and journey completion
  useEffect(() => {
    if (
      positionHistory.length < DIRECTION_CHECK_THRESHOLD ||
      directionCheckRef.current
    )
      return;
    directionCheckRef.current = true;
    const currentPosition = positionHistory[positionHistory.length - 1];
    
    // Use the imported checkJourneyComplete function
    const isComplete = checkJourneyComplete(
      currentPosition,
      stops,
      pathDirection,
      { before: [], after: [] }, // You may need to pass actual routePaths
      [], // fullPath
      true, // isMoving
      0 // stoppedDuration
    );
    
    if (pathDirection && isComplete) {
      console.log("Journey completed, resetting direction");
      setPathDirection(null);
      setPositionHistory([]);
      if (vehicle?.device_id) {
        localStorage.removeItem(`pathDirection_${vehicle.device_id}`);
      }
      setTimeout(() => {
        const newDirection = determineInitialDirection(currentPosition, stops);
        if (newDirection) {
          console.log(`Starting new journey in direction: ${newDirection}`);
          setPathDirection(newDirection);
          savePathDirection(vehicle.device_id, newDirection);
        }
      }, 3000);
    } else if (!pathDirection) {
      const newDirection = checkDirectionChange(positionHistory, stops);
      if (newDirection && vehicle?.device_id) {
        console.log(`Setting new journey direction: ${newDirection}`);
        setPathDirection(newDirection);
        savePathDirection(vehicle.device_id, newDirection);
      }
    }
    setTimeout(() => {
      directionCheckRef.current = false;
    }, 5000);
  }, [
    positionHistory,
    pathDirection,
    vehicle?.device_id,
    stops,
    checkDirectionChange,
    checkJourneyComplete,
    setPathDirection,
    setPositionHistory,
  ]);
};